// NEW_FILE_JUSTIFIED: New messages feature component
import { memo } from 'react';
import { Filter } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { ConversationItem } from './ConversationItem';
import type { IConversationListProps, TConversationFilter } from '@/@types/messages';

const FILTER_TABS: { label: string; value: TConversationFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Personal', value: 'personal' },
  { label: 'Groups', value: 'groups' },
  { label: 'Unread', value: 'unread' },
];

function ConversationListBase({
  conversations,
  selectedId,
  onSelect,
  filter,
  onFilterChange,
  searchQuery,
  onSearchChange,
}: IConversationListProps) {
  return (
    <nav
      aria-label="Conversation list"
      className="w-100 flex flex-col h-full border-r border-msg-panel-border bg-msg-panel-bg shrink-0 overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <h2 className="text-base font-semibold text-foreground">All Conversations</h2>
        <button
          type="button"
          disabled
          className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center text-muted-foreground opacity-50 cursor-not-allowed"
          aria-label="Filter conversations (coming soon)"
        >
          <Filter size={16} />
        </button>
      </div>

      {/* Search bar */}
      <div className="px-4">
        <label htmlFor="conversation-search" className="sr-only">
          Search conversations
        </label>
        <Input
          id="conversation-search"
          type="search"
          placeholder="Search conversations..."
          value={searchQuery}
          onChange={e => onSearchChange(e.target.value)}
          className="rounded-lg bg-background-input border-msg-panel-border"
        />
      </div>

      {/* Filter tabs */}
      <div
        role="tablist"
        aria-label="Conversation filter"
        className="flex items-center gap-1 px-4 pt-3"
      >
        {FILTER_TABS.map(tab => (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={filter === tab.value}
            onClick={() => onFilterChange(tab.value)}
            className={cn(
              'flex-1 text-center rounded-lg py-2 px-3 text-sm transition-colors',
              filter === tab.value
                ? 'bg-muted text-foreground font-semibold'
                : 'text-muted-foreground font-medium hover:text-foreground'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Divider */}
      <div className="h-px w-full bg-msg-panel-border mt-3" />

      {/* Conversation list — plain overflow-y instead of Radix ScrollArea
           to avoid display:table wrapper breaking flex badge layout */}
      <div className="flex-1 overflow-y-auto">
        <ul className="py-1 list-none">
          {conversations.map(conversation => (
            <li key={conversation.id}>
              <ConversationItem
                conversation={conversation}
                isSelected={conversation.id === selectedId}
                onSelect={onSelect}
              />
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}

export const ConversationList = memo(ConversationListBase);
